import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useAppRoleCreateMutation } from "@/common/api/app-role/use-app-role-create.mutation";
import { useGroupSearchQuery } from "@/common/api/group/use-group-search.query";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { FormCard } from "@/components/ui/form-card";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { AppRoleFormFields, AppRoleGroupPageData } from "./_components/app-role-form-fields";
import { appRoleFormSchema, AppRoleFormSchemaType } from "./_helpers/app-role-form.helpers";

export function AppRoleCreateView() {
  const { t } = useTranslation(["ram", "common"]);
  const [_, navigate] = useLocation();
  const params = useParams<{ applicationUuid: string }>();
  const { isPending, mutateAsync } = useAppRoleCreateMutation();

  const formId = useId();
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data: groupPageData, isLoading } = useGroupSearchQuery({
    options: {
      parameters: { search: searchTerm, page, pageSize },
    },
  });

  const pageData: AppRoleGroupPageData = {
    data: (groupPageData?.data ?? []).map((group) => ({
      uuid: group.uuid,
      name: group.name,
      hasAppRole: false,
    })),
    meta: groupPageData?.meta,
  };

  const form = useForm<AppRoleFormSchemaType>({
    resolver: zodResolver(appRoleFormSchema),
    mode: "onChange",
    defaultValues: { name: "" },
  });

  const handlePaginationChange = useCallback(({ page, pageSize }: { page: number; pageSize: number }) => {
    setPage(page);
    setPageSize(pageSize);
  }, []);

  const handleCancel = () => navigate(`~/ram/application/${params.applicationUuid}/app-role`);

  const handleSave = async (groupUuidsToAdd: string[]): Promise<void> => {
    const isValid = await form.trigger();

    if (!isValid) return;

    const nameValue = form.getValues("name");

    await toastMutationStatus({
      mutationFn: () =>
        mutateAsync({ applicationUuid: params.applicationUuid, name: nameValue, groups: groupUuidsToAdd }),
      successMessage: t("ram:PermissionHasBeenAdded"),
      errorMessage: t("ram:ErrorInCreationOfThePermission"),
    });

    navigate(`~/ram/application/${params.applicationUuid}/app-role`);
  };

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Table}
      title={t("ram:AddPermissions")}
      subTitle={t("common:CompleteRequiredInformationDescription")}
    >
      <div className="space-y-6">
        <FormCard title={t("ram:AddPermissions")} titleHidden>
          <Form {...form}>
            <AppRoleFormFields
              formId={formId}
              form={form}
              pageData={pageData}
              isLoading={isLoading}
              page={page}
              pageSize={pageSize}
              onSearchChange={setSearchTerm}
              onPaginationChange={handlePaginationChange}
              onSave={handleSave}
              onSaveDisabledChange={setIsSaveDisabled}
            />
          </Form>
        </FormCard>

        <div className="flex flex-wrap justify-end gap-4">
          <Button
            type="button"
            variant={ButtonVariant.Outlined}
            colorVariant={ButtonColorVariant.Secondary}
            onClick={handleCancel}
          >
            {t("common:Cancel")}
          </Button>
          <Button type="submit" form={formId} disabled={isSaveDisabled || isPending}>
            {t("common:Save")}
          </Button>
        </div>
      </div>
    </CenteredLayout>
  );
}
